import React, { useContext } from 'react';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { FooterModel } from '@/types';
import { Link, MetadataContext, ResponsiveImage, SocialImage } from '@/components';
import { SystemFeaturesContext } from '@/components/ContextProviders';
import GooTrans from './GoogleTranslate';

const FooterApp = ({ quickLinks, logo, address, secondAddress, contact, socialLinks, footerLinks }: FooterModel) => {
  const t = useTranslations();
  const { pageType } = useContext(MetadataContext);
  const features = useContext(SystemFeaturesContext);

  return (
    <div
      className={classNames(
        'mt-auto print:hidden',
        (pageType === 'legacyReportPage' || pageType === 'reportPage') && 'xxl:mb-18 mb-14',
      )}
    >
      <footer className="module-base-light flex flex-col overflow-hidden bg-[#262626] pt-16 pb-8 lg:py-0">
        {features.googleTranslate && (
          <GooTrans />
        )}
        {quickLinks && quickLinks.length > 0 && (
          <>
            <div className="color-from-bg order-3 container lg:hidden">
              <p className="pt-10 pb-10 text-lg leading-[22px] font-bold">{t('footer.$quickLinks')}</p>
            </div>
            <div className="color-from-bg order-4 container flex flex-col justify-between lg:order-1 lg:flex-row lg:py-9">
              {quickLinks.map((link, index) => (
                <Link className="btn !justify-start underline pointer-coarse:h-10" key={index} link={link} />
              ))}
            </div>
            <div className="order-2 px-4 lg:order-2">
              <div className="h-px w-full bg-white/10"></div>
            </div>
          </>
        )}
        <div className="color-from-bg order-1 container flex flex-col gap-8 py-6 lg:order-3 lg:flex-row lg:gap-4 lg:py-12">
          <div className="flex w-full flex-col items-center lg:w-1/4 lg:items-start">
            <Link link={{ href: '/' }} className="mb-8 block w-[130px] md:mb-8 lg:mb-4">
              <ResponsiveImage image={logo} />
            </Link>
            <p className="pt-3 pb-4 text-lg leading-[22px] font-bold">{t('footer.$followUs')}</p>
            <div className="flex gap-3">
              {socialLinks?.map((link, index) => (
                <Link key={index} link={{ ...link, target: '_blank' }} className="py-2">
                  <SocialImage type={link.text} className="h-6 w-6" />
                </Link>
              ))}
            </div>
          </div>
          <div className="flex w-full flex-col items-center lg:w-2/4 lg:items-start">
            <p className="pb-10 text-lg leading-[22px] font-bold">{t('footer.$findUs')}</p>
            <div className="flex w-full flex-col gap-4 font-medium lg:flex-row [&_p]:pb-4">
              <div
                className="body-1 w-full text-center lg:w-1/2 lg:text-left"
                dangerouslySetInnerHTML={{ __html: address }}
              />
              <div
                className="body-1 w-full text-center lg:w-1/2 lg:text-left"
                dangerouslySetInnerHTML={{ __html: secondAddress }}
              />
            </div>
          </div>
          <div className="w-full lg:w-1/4">
            <p className="pb-9 text-center text-lg leading-[22px] font-bold lg:text-left">{t('footer.$contact')}</p>
            <div
              className={classNames(
                'contact-section body-1 w-full pl-6 text-center font-medium lg:text-left [&_.cd]:pb-3',
                '[&_.mail]:relative [&_.mail]:before:absolute [&_.mail]:before:-left-7 [&_.mail]:before:h-6 [&_.mail]:before:w-6 [&_.mail]:before:bg-[url("../assets/envelopeSimpleLight.svg")] [&_.mail]:before:bg-contain [&_.mail]:before:opacity-50 [&_.mail]:before:invert [&_.mail_a]:!no-underline',
                '[&_.phone]:relative [&_.phone]:before:absolute [&_.phone]:before:-top-1 [&_.phone]:before:-left-7 [&_.phone]:before:h-6 [&_.phone]:before:w-6 [&_.phone]:before:bg-[url("../assets/deviceMobileLight.svg")] [&_.phone]:before:bg-contain [&_.phone]:before:opacity-50 [&_.phone]:before:invert [&_.phone_a]:!no-underline',
              )}
              dangerouslySetInnerHTML={{ __html: contact }}
            />
          </div>
        </div>
        {footerLinks && (
          <>
            <div className="hidden px-4 lg:order-4 lg:block">
              <div className="h-px w-full bg-white/10"></div>
            </div>
            <div className="color-from-bg order-5 container flex flex-col justify-between lg:flex-row lg:py-9">
              {footerLinks.map((link, index) => (
                <Link className="btn !justify-start underline pointer-coarse:h-10" key={index} link={link} />
              ))}
            </div>
          </>
        )}
      </footer>
    </div>
  );
};

export default FooterApp;
